import React, { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { 
  useGetPropertyDepositsQuery, 
  useGetPropertyUnitsTenantQuery, 
  usePostPropertyDepositsMutation,
  useUpdatePropertyDepositsMutation 
} from '../../lib/apiSlice/subscription/subscriptionSlice.tsx';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export default function DepositReport() {
  const id = useParams().id;
  const isAllProperties = id === 'all';
  const { data: DepositData, isLoading, isError, refetch: refetchDeposits } = useGetPropertyDepositsQuery(id);
  const { data: housesData, isLoading: isLoadingHouses, isError: isErrorHouses } = useGetPropertyUnitsTenantQuery(id);
  const [addUnitDeposit] = usePostPropertyDepositsMutation();
  const [updateUnitDeposit] = useUpdatePropertyDepositsMutation();
  
  // Use the actual API data instead of dummy data
  const deposits = DepositData?.results || [];
  const houses = housesData?.houses || [];
  const tenants = housesData?.tenants || [];

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed to 10 items per page
  const [refundModal, setRefundModal] = useState({ 
    show: false, 
    deposit: null, 
    refundAmount: '' 
  });
  const [addDepositModal, setAddDepositModal] = useState({
    show: false,
    houseId: '',
    userId: '',
    useCurrentTenant: true,
    selectedTenant: '',
    newTenantName: '',
    depositAmount: '',
    description: ''
  });

  // Filter deposits based on search term
  const filteredDeposits = useMemo(() => {
    if (!deposits) return [];

    return deposits.filter(deposit =>
      deposit.house_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deposit.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (deposit.refunded ? 'refunded' : 'pending').includes(searchTerm.toLowerCase()) ||
      deposit.deposit_amount?.toString().includes(searchTerm) ||
      deposit.deposit_description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [deposits, searchTerm]);

  // Pagination calculations
  const paginatedDeposits = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredDeposits.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredDeposits, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredDeposits.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  // Handle refund action
  const handleRefund = (deposit) => {
    setRefundModal({ 
      show: true, 
      deposit, 
      refundAmount: '' 
    });
  };

  // Process refund
  const processRefund = async () => {
    const { deposit, refundAmount } = refundModal;
    const amount = parseFloat(refundAmount);

    if (!amount || amount <= 0 || amount > deposit.deposit_amount) {
      toast.error("Please enter a valid refund amount");
      return;
    }

    try {
      await updateUnitDeposit({
        depositId: deposit.id,
        unitDepositData: {
          refunded_amount: amount,
          refunded: amount === deposit.deposit_amount,
        },
      }).unwrap();

      toast.success("Deposit updated successfully");
      refetchDeposits(); // Refresh the deposits data
      setRefundModal({ show: false, deposit: null, refundAmount: '' });
    } catch (err) {
      console.error("Failed to update deposit:", err);
      toast.error("Failed to update deposit. Please try again.");
    }
  };

  // Handle adding new deposit
  const handleAddDeposit = async () => {
    if (isAllProperties) {
      toast.error('Please select a specific property before adding a deposit.');
      return;
    }
    const { houseId, selectedTenant, depositAmount, description } = addDepositModal;

    // Validation
    if (!houseId || !depositAmount || !description || !selectedTenant) {
      toast.error('Please fill all required fields');
      return;
    }

    const amount = parseInt(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid deposit amount');
      return;
    }

    const data = {
      house: houseId,
      apartment: id,
      user: selectedTenant,
      deposit_amount: amount,
      deposit_description: description,
    };

    try {
      await addUnitDeposit(data).unwrap();
      toast.success('Successfully added the deposit');
      refetchDeposits(); // Refresh the deposits data
      
      setAddDepositModal({
        show: false,
        houseId: '',
        userId: '',
        useCurrentTenant: true,
        selectedTenant: '',
        newTenantName: '',
        depositAmount: '',
        description: ''
      });
    } catch (err) {
      console.error('Add deposit error:', err);
      toast.error('Something went wrong while trying to add the deposit. Please try again.');
    }
  };

  // Reset pagination when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // CSV data for export (export all filtered data, not just current page)
  const csvData = filteredDeposits.map(deposit => ({
    House: deposit.house_number || '',
    Apartment: deposit.apartment_name || '',
    User: deposit.user_name || '',
    'Deposit Amount': deposit.deposit_amount || 0,
    'Refunded Status': deposit.refunded ? 'Refunded' : 'Pending',
    'Refunded Amount': deposit.refunded_amount || 0,
    'Description': deposit.deposit_description || ''
  }));

  if (isLoading || isLoadingHouses) {
    return (
      <div className="flex justify-center items-center p-6">
        <div className="text-gray-600">Loading deposits...</div>
      </div>
    );
  }

  if (isError || isErrorHouses) {
    return (
      <div className="flex justify-center items-center p-6">
        <div className="text-red-600">Error loading deposits. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Search and Action Controls */}
      <div className="flex sm:flex-row flex-col justify-between items-center gap-4 mb-6">
        <div className="w-full sm:w-64">
          <input
            type="text"
            placeholder="Search house, user, status, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
          />
        </div>

        <div className="flex gap-3 flex-wrap justify-center">
          <button
            onClick={() => setAddDepositModal({
              show: true,
              houseId: '',
              userId: '',
              useCurrentTenant: true,
              selectedTenant: '',
              newTenantName: '',
              depositAmount: '',
              description: ''
            })}
            disabled={isAllProperties}
            className={`px-4 py-2 rounded-lg text-white transition-colors whitespace-nowrap ${isAllProperties ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
          >
            Add New Deposit
          </button>

          <CSVLink
            data={csvData}
            filename={`deposit-report-${new Date().toISOString().split('T')[0]}.csv`}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white no-underline transition-colors whitespace-nowrap"
          >
            Export to CSV
          </CSVLink>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {paginatedDeposits.length} of {filteredDeposits.length} deposits
        {searchTerm && ` (filtered from ${deposits.length} total)`}
      </div>

      {/* Deposit Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">House</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Apartment</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">User</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Deposit Amount</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Refunded Status</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Refunded Amount</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedDeposits.map((deposit) => (
                <tr key={deposit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">
                    {deposit.house_number || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                    {deposit.apartment_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                    {deposit.user_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                    Ksh {(deposit.deposit_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 max-w-xs text-gray-500 text-sm">
                    <div className="truncate" title={deposit.deposit_description}>
                      {deposit.deposit_description || 'No description'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      deposit.refunded ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {deposit.refunded ? 'Refunded' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                    Ksh {(deposit.refunded_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 font-medium text-sm whitespace-nowrap">
                    {!deposit.refunded && (
                      <button
                        onClick={() => handleRefund(deposit)}
                        className="bg-blue-100 hover:bg-blue-200 px-3 py-1 rounded-md font-medium text-blue-600 hover:text-blue-900 text-xs transition-colors"
                      >
                        Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedDeposits.length === 0 && (
          <div className="py-8 text-gray-500 text-center">
            {deposits.length === 0 ? 'No deposits found.' : 'No deposits found matching your search.'}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-1">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md text-sm ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                First
              </button>

              {/* Previous Page */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md text-sm ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}

              {/* Next Page */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md text-sm ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Next
              </button>

              {/* Last Page */}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md text-sm ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Refund Modal */}
      {refundModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4">
            <h2 className="mb-4 font-semibold text-lg">Process Refund</h2>
            <p className="mb-2 text-gray-600 text-sm">
              House: <span className="font-medium">{refundModal.deposit.house_number}</span>
            </p>
            <p className="mb-4 text-gray-600 text-sm">
              User: <span className="font-medium">{refundModal.deposit.user_name}</span>
            </p>

            <div className="mb-4">
              <label className="block mb-1 font-medium text-gray-700 text-sm">
                Refund Amount (Max: Ksh {refundModal.deposit.deposit_amount.toLocaleString()})
              </label>
              <input
                type="number"
                value={refundModal.refundAmount}
                onChange={(e) => setRefundModal({ ...refundModal, refundAmount: e.target.value })}
                min="0"
                max={refundModal.deposit.deposit_amount}
                step="0.01"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                placeholder="Enter refund amount"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRefundModal({ show: false, deposit: null, refundAmount: '' })}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={processRefund}
                disabled={
                  !refundModal.refundAmount ||
                  parseFloat(refundModal.refundAmount) <= 0 ||
                  parseFloat(refundModal.refundAmount) > refundModal.deposit.deposit_amount
                }
                className={`px-4 py-2 rounded-md text-white ${
                  !refundModal.refundAmount ||
                  parseFloat(refundModal.refundAmount) <= 0 ||
                  parseFloat(refundModal.refundAmount) > refundModal.deposit.deposit_amount
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Process Refund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Deposit Modal */}
      {addDepositModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 font-semibold text-lg">Add New Deposit</h2>

            <div className="space-y-4">
              {/* House Selection */}
              <div>
                <label className="block mb-1 font-medium text-gray-700 text-sm">House *</label>
                <select
                  value={addDepositModal.houseId}
                  onChange={(e) => setAddDepositModal({
                    ...addDepositModal,
                    houseId: e.target.value,
                    selectedTenant: '' // Reset tenant selection when house changes
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  required
                >
                  <option value="">Select House</option>
                  {houses.map(house => (
                    <option key={house.house_id} value={house.house_id}>
                      {house.house_number}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tenant Selection */}
              <div>
                <label className="block mb-1 font-medium text-gray-700 text-sm">Select Tenant *</label>
                <select
                  value={addDepositModal.selectedTenant}
                  onChange={(e) => setAddDepositModal({ ...addDepositModal, selectedTenant: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  required
                >
                  <option value="">Select Tenant</option>
                  {tenants.map(tenant => (
                    <option key={tenant.user_id} value={tenant.user_id}>
                      {tenant.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Deposit Amount */}
              <div>
                <label className="block mb-1 font-medium text-gray-700 text-sm">Deposit Amount *</label>
                <input
                  type="number"
                  value={addDepositModal.depositAmount}
                  onChange={(e) => setAddDepositModal({ ...addDepositModal, depositAmount: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  placeholder="Enter deposit amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block mb-1 font-medium text-gray-700 text-sm">Description *</label>
                <textarea
                  value={addDepositModal.description}
                  onChange={(e) => setAddDepositModal({ ...addDepositModal, description: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  placeholder="Enter deposit description"
                  rows="3"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAddDepositModal({
                  show: false,
                  houseId: '',
                  userId: '',
                  useCurrentTenant: true,
                  selectedTenant: '',
                  newTenantName: '',
                  depositAmount: '',
                  description: ''
                })}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDeposit}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md text-white"
              >
                Add Deposit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
